import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import StatsPanel from './StatsPanel';
import QuestList from './QuestList';
import LevelUpModal from './LevelUpModal';
import CreateQuestModal from './CreateQuestModal';
import AchievementModal from './AchievementModal';
import DailyResetTimer from './DailyResetTimer';
import Sidebar from './Sidebar';
import QuestCompleteAnimation from './QuestCompleteAnimation';
import SystemNotification from './SystemNotification';
import PenaltyWarningModal from './PenaltyWarningModal';
import PenaltyBanner from './PenaltyBanner';
import DailyRewardModal from './DailyRewardModal';
import StatAllocationModal from './StatAllocationModal';
import AIQuestGenerator from './AIQuestGenerator';
import ProgramCreationModal from './ProgramCreationModal';
import ActiveProgramsPanel from './ActiveProgramsPanel';
import ProgramRoadmapModal from './ProgramRoadmapModal';
import AIGeneratorSelectorModal from './AIGeneratorSelectorModal';
import OnboardingOverlay from './OnboardingOverlay';
import TrialExpiredModal from './TrialExpiredModal';
import SubscriptionModal from './SubscriptionModal';
import EmailVerificationBanner from './EmailVerificationBanner';
import HelpBubble from './HelpBubble';
import BackgroundRenderer from './BackgroundRenderer';
import ShopPage from './pages/ShopPage';
import FocusPage from './pages/FocusPage';
import StatsPage from './pages/StatsPage';
import HistoryPage from './pages/HistoryPage';
import AchievementsPage from './pages/AchievementsPage';
import SettingsPage from './pages/SettingsPage';
import CalendarPage from './pages/CalendarPage';
import PassiveSkillsPage from './pages/PassiveSkillsPage';
import { soundService } from '../services/soundService';
import { hapticService } from '../services/hapticService';
import { trackPageView, trackDailyRewardClaimed, trackLevelUp } from '../services/analyticsService';

interface DashboardProps {
  showEmailVerificationBanner?: boolean;
  user: User | null;
  initialPage?: 'quests' | 'focus' | 'passiveSkills' | 'shop' | 'stats' | 'history' | 'achievements' | 'settings' | 'calendar';
}

const Dashboard: React.FC<DashboardProps> = ({ showEmailVerificationBanner = false, user, initialPage = 'quests' }) => {
  const { profile, quests, programs, levelUpQueue, achievements, achievementQueue, questCompleteData, clearQuestComplete, notifications, removeNotification, showPenaltyModal, failedDailyQuests, acceptPenalty, dailyReward, showDailyRewardModal, claimDailyReward, allocateStatPoints, completeQuest, createQuest, createQuestsBatch, updateQuest, toggleSubTask, deleteQuest, clearLevelUpQueue, clearAchievementQueue, createProgram, pauseProgram, resumeProgram, deleteProgram, getEffectiveStats, getBackgroundSettings, signOut, isDemoMode } = useUser();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAISelectorOpen, setIsAISelectorOpen] = useState(false);
  const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
  const [isProgramCreationOpen, setIsProgramCreationOpen] = useState(false);
  const [isProgramRoadmapOpen, setIsProgramRoadmapOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  // On mobile, start with sidebar hidden (collapsed). On desktop, start with it visible.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 1024);
  const [currentPage, setCurrentPage] = useState<'quests' | 'focus' | 'passiveSkills' | 'shop' | 'stats' | 'history' | 'achievements' | 'settings' | 'calendar'>(initialPage);
  const [isStatAllocationOpen, setIsStatAllocationOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'profile' | 'audio' | 'preferences' | 'background' | 'account'>('profile');
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isFocusFullscreen, setIsFocusFullscreen] = useState(false);
  const [isPassiveSkillsCreateModalOpen, setIsPassiveSkillsCreateModalOpen] = useState(false);
  const [isPassiveSkillsMilestoneModalOpen, setIsPassiveSkillsMilestoneModalOpen] = useState(false);
  
  // Detect mobile device for performance optimizations
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Check if subscription is expired
  const isSubscriptionExpired = profile?.subscription?.tier === 'expired';
  
  // Onboarding state - Disabled on mobile devices (screens <= 1024px)
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const isMobile = window.innerWidth <= 1024;
    const hasCompletedOnboarding = localStorage.getItem('onboarding_completed') === 'true';
    
    // Skip onboarding on mobile devices
    if (isMobile) {
      // Mark as completed so it doesn't show if they switch to desktop
      if (!hasCompletedOnboarding) {
        localStorage.setItem('onboarding_completed', 'true');
      }
      return false;
    }
    
    return !hasCompletedOnboarding;
  });
  
  // Force sidebar to be open during onboarding
  useEffect(() => {
    if (showOnboarding) {
      setIsSidebarCollapsed(false);
    }
  }, [showOnboarding]);
  
  // Initialize background music on component mount
  useEffect(() => {
    soundService.initializeMusic();
  }, []);
  
  const handleCompleteOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
    // Restore sidebar state based on screen size
    setIsSidebarCollapsed(window.innerWidth < 1024);
  };
  
  const handleSkipOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
    // Restore sidebar state based on screen size
    setIsSidebarCollapsed(window.innerWidth < 1024);
  };

  // Show loading state while profile is being loaded
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-float">⚔️</div>
          <div className="flex items-center justify-center gap-3">
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
            <span className="font-orbitron text-sm tracking-wider text-system-blue uppercase">
              ［ Loading Your Data ］
            </span>
          </div>
        </div>
      </div>
    );
  }

  const xpPercentage = (profile.currentXP / profile.xpToNextLevel) * 100;

  const handleNavigation = (page: string) => {
    setCurrentPage(page as typeof currentPage);
    // Haptic feedback for page navigation
    hapticService.pageChange();

    // Map page names to proper route paths
    const pageRouteMap: Record<string, string> = {
      'quests': '/dashboard',
      'focus': '/dashboard/focus',
      'passiveSkills': '/dashboard/passive-skills',
      'shop': '/dashboard/shop',
      'stats': '/dashboard/stats',
      'history': '/dashboard/history',
      'achievements': '/dashboard/achievements',
      'settings': '/dashboard/settings',
      'calendar': '/dashboard/calendar'
    };

    const routePath = pageRouteMap[page] || `/dashboard/${page}`;
    const pageTitle = `Dashboard - ${page.charAt(0).toUpperCase() + page.slice(1)}`;

    // Navigate to the new route
    navigate(routePath);

    // Track page navigation with proper route path
    trackPageView(routePath, pageTitle);
  };

  const handleOpenSubscriptionSettings = () => {
    setSettingsInitialTab('account');
    setCurrentPage('settings');
    navigate('/dashboard/settings');
  };

  // Wrapper for daily reward claim with analytics
  const handleClaimDailyReward = () => {
    if (dailyReward) {
      const rewardType = dailyReward.bonusReward?.type || 'standard';
      trackDailyRewardClaimed(rewardType, dailyReward.streakDay);
    }
    // Haptic feedback for claiming reward
    hapticService.success();
    claimDailyReward();
  };

  // Wrapper for level up with analytics
  const handleLevelUp = () => {
    if (profile) {
      trackLevelUp(profile.level, profile.totalXP);
    }
    // Haptic feedback for level up
    hapticService.levelUp();
    clearLevelUpQueue();
  };

  // Key for triggering page transitions
  const pageKey = currentPage;

  // Hide floating UI (hamburger, help bubble) when any modal is open
  const isAnyModalOpen = (
    isCreateModalOpen ||
    isAISelectorOpen ||
    isAIGeneratorOpen ||
    isProgramCreationOpen ||
    isProgramRoadmapOpen ||
    isStatAllocationOpen ||
    (levelUpQueue.length > 0) ||
    (achievementQueue.length > 0) ||
    showPenaltyModal ||
    showDailyRewardModal ||
    isSubscriptionModalOpen ||
    isPassiveSkillsCreateModalOpen ||
    isPassiveSkillsMilestoneModalOpen
  );

  return (
    <div className="min-h-screen relative">
      {/* Custom Background Renderer */}
      <BackgroundRenderer settings={getBackgroundSettings()} />
      
      {/* Email Verification Banner - Persistent, no close button */}
      {showEmailVerificationBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100]">
          <EmailVerificationBanner user={user} />
        </div>
      )}

      {/* Mobile Hamburger Button - Hide on Focus Fullscreen or when any modal is open */}
      {!isFocusFullscreen && !isAnyModalOpen && (
        <button
          onClick={() => {
            hapticService.tap();
            setIsSidebarCollapsed(!isSidebarCollapsed);
          }}
          className={`fixed ${showEmailVerificationBanner ? 'top-[88px]' : 'top-4'} right-4 z-[60] lg:hidden p-3 bg-system-darker border-2 border-system-blue/50 rounded-lg hover:bg-system-blue/20 transition-all shadow-neon-blue`}
          aria-label="Toggle Menu"
        >
          <svg className="w-6 h-6 text-system-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isSidebarCollapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
        </button>
      )}

      {/* Sidebar Navigation - Hide on Focus Fullscreen */}
      {!isFocusFullscreen && (
        <Sidebar 
          onNavigate={handleNavigation}
          achievementCount={achievements.filter(a => a.unlocked).length}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activePage={currentPage}
        />
      )}
      
      {/* Animated Background Grid - Hide on Focus Fullscreen */}
      {!isFocusFullscreen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="absolute inset-0 opacity-10">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute h-px bg-gradient-to-r from-transparent via-system-blue to-transparent"
                style={{
                  top: `${i * 5}%`,
                  left: 0,
                  right: 0,
                  animation: `pulse ${3 + i * 0.2}s ease-in-out infinite`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Header - System Status - Hide on Focus Fullscreen */}
      {!isFocusFullscreen && (
        <div 
          className={`relative transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}
          style={{ 
            zIndex: 10
          }}
        >
          <div 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl mx-3 sm:mx-6 my-3 sm:my-6"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
          <div className="w-full px-3 sm:px-6 py-3 sm:py-8">
            {/* Top Bar - System Label */}
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6 ml-16 lg:ml-0">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="font-orbitron text-[10px] sm:text-sm tracking-wider text-blue-400 uppercase">
                {t.dashboard.systemStatus}
              </span>
            </div>

            {/* Player Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 mb-3 sm:mb-6">
              {/* Left - Avatar, Name & Title */}
              <div className="space-y-1.5 sm:space-y-2">
                {/* Avatar and Name */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {profile.avatar ? (
                    <div className="relative">
                      <img
                        src={profile.avatar}
                        alt="Player Avatar"
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-xl border-2 border-slate-500/50"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl border border-blue-500/50 flex items-center justify-center font-orbitron font-bold text-lg sm:text-2xl">
                      {profile.name[0]?.toUpperCase() || 'H'}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <div className="flex items-baseline gap-1.5 sm:gap-3 flex-wrap">
                      <span className="text-[10px] sm:text-sm text-gray-400 font-rajdhani uppercase tracking-wide">{t.dashboard.name}</span>
                      <h1 className="text-xl sm:text-4xl font-bold font-orbitron text-white">
                        {profile.name}
                      </h1>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
                  <span className="text-[10px] sm:text-sm text-gray-400 font-rajdhani uppercase tracking-wide">{t.dashboard.class}</span>
                  <p className="text-base sm:text-2xl font-semibold font-orbitron text-white">
                    {profile.currentTitle}
                  </p>
                </div>
                {/* Streak & Currency Display */}
                <div className="flex items-center gap-2 sm:gap-6 mt-2 sm:mt-3 flex-wrap">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-lg sm:text-2xl animate-pulse">🔥</span>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg sm:text-2xl font-bold font-orbitron text-orange-400">
                          {profile.streak}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase">{t.dashboard.streak}</span>
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-500 font-rajdhani">
                        {t.dashboard.best} {profile.longestStreak}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <img 
                          src="/icons/icon-gold.png" 
                          alt="Gold"
                          className="w-5 h-5 sm:w-8 sm:h-8 object-contain"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(252, 211, 77, 0.8))' }}
                        />
                        <span className="text-xl sm:text-3xl font-black font-orbitron text-yellow-400">
                          {profile.currency}
                        </span>
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani ml-1">
                        {t.dashboard.currency}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Level & XP */}
              <div className="flex flex-col items-start md:items-end justify-center" data-onboarding="level-info">
                <div className="text-left md:text-right mb-1.5 sm:mb-2">
                  <div className="text-[10px] sm:text-sm text-gray-400 font-rajdhani uppercase tracking-wide mb-0.5 sm:mb-1">
                    {t.dashboard.level}
                  </div>
                  <div className="text-4xl sm:text-7xl font-black font-orbitron text-white leading-none">
                    {profile.level}
                  </div>
                </div>
                <div className="text-[10px] sm:text-sm text-gray-400 font-rajdhani mb-2 sm:mb-3">
                  <span className="text-white font-semibold">{profile.currentXP}</span>
                  {' / '}
                  <span className="text-system-blue font-semibold">{profile.xpToNextLevel}</span>
                  {' '}{t.dashboard.xp}
                </div>
                {/* Daily Reset Timer */}
                <DailyResetTimer />
              </div>
            </div>

            {/* XP Bar */}
            <div className="relative">
              <div 
                className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/70 border-b-slate-700/70 rounded-2xl h-5 sm:h-6 overflow-hidden"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -1px 3px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Progress bar */}
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 transition-all duration-500 relative overflow-hidden"
                  style={{ width: `${xpPercentage}%` }}
                >
                  {/* Animated shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30 animate-pulse" />
                </div>
                {/* XP Text Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] sm:text-xs font-bold font-orbitron tracking-wider text-white">
                    {Math.round(xpPercentage)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Main Content */}
      <div 
        className={`relative transition-all duration-300 ${isFocusFullscreen ? '' : (isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64')}`}
        style={{ 
          zIndex: 10
        }}
      >
        <div className={isFocusFullscreen ? '' : 'max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8'}>
        {/* Render different pages based on currentPage */}
        {currentPage === 'quests' && (
          <div key={pageKey} className="page-enter">
            {/* System Label & Quest Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="font-orbitron text-[10px] sm:text-sm tracking-wider text-blue-400 uppercase">
                  {t.dashboard.activeQuests}
                </span>
              </div>
              
              <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap w-full sm:w-auto" data-onboarding="quest-buttons">
                {/* AI Generator Button (Unified) */}
                <button
                  onClick={() => {
                    hapticService.click();
                    // Check if we're in demo mode
                    if (isDemoMode) {
                      // Show signup prompt for AI generator in demo mode
                      alert('🔒 Sign up to unlock AI quest generation and save your progress!');
                      return;
                    }
                    setIsAISelectorOpen(true);
                  }}
                  className="relative group bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-3 sm:px-5 py-2 sm:py-3 font-orbitron font-bold flex-1 sm:flex-initial hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/20 transition-all hover:scale-105"
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -1px 3px rgba(0, 0, 0, 0.3)'
                  }}
                  data-onboarding="ai-generator"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="tracking-wider text-xs sm:text-sm uppercase">［ {t.aiSelector.title} ］</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                </button>

                {/* Manual Quest Button */}
                <button
                  onClick={() => {
                    hapticService.click();
                    setIsCreateModalOpen(true);
                  }}
                  className="relative group bg-gradient-to-b from-blue-800/40 via-blue-900/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-2xl px-3 sm:px-5 py-2 sm:py-3 font-orbitron font-bold flex-1 sm:flex-initial hover:border-blue-500/60 hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(100, 150, 255, 0.15), inset 0 -1px 3px rgba(30, 58, 138, 0.4)'
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="tracking-wider text-xs sm:text-sm uppercase">［ {t.dashboard.createQuest} ］</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                </button>
              </div>
            </div>

            {/* Penalty Warning Banner */}
            <PenaltyBanner />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
              {/* Left Column - Stats */}
              <div className="lg:col-span-1 animate-slide-in-left space-y-4 sm:space-y-6">
                <div data-onboarding="stats-panel">
                  <StatsPanel 
                    stats={getEffectiveStats()}
                    unallocatedPoints={profile.unallocatedStatPoints}
                    onAllocateClick={() => {
                      hapticService.click();
                      setIsStatAllocationOpen(true);
                    }}
                  />
                </div>
              </div>

              {/* Right Column - Quests */}
              <div className="lg:col-span-2 animate-slide-in-right">
                <QuestList
                  quests={quests}
                  onComplete={(questId) => {
                    hapticService.questComplete();
                    completeQuest(questId);
                  }}
                  onDelete={(questId) => {
                    hapticService.tap();
                    deleteQuest(questId);
                  }}
                  onUpdate={updateQuest}
                  onToggleSubTask={(questId, subTaskId) => {
                    hapticService.tick();
                    toggleSubTask(questId, subTaskId);
                  }}
                />
              </div>
            </div>

            {/* Active Programs Section - Full Width */}
            {programs && programs.length > 0 && (
              <div className="animate-slide-in-left" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="font-orbitron text-[10px] sm:text-sm tracking-wider text-blue-400 uppercase">
                    {t.programs.activePrograms}
                  </span>
                </div>
                  <ActiveProgramsPanel
                    programs={programs}
                    onViewRoadmap={(program) => {
                      setSelectedProgram(program);
                      setIsProgramRoadmapOpen(true);
                    }}
                    onPauseProgram={pauseProgram}
                    onResumeProgram={resumeProgram}
                    onDeleteProgram={deleteProgram}
                  />
              </div>
            )}
          </div>
        )}

        {currentPage === 'focus' && (
          <FocusPage onRequestFullscreen={setIsFocusFullscreen} />
        )}

        {currentPage === 'passiveSkills' && (
          <div key={pageKey} className="page-enter">
            <PassiveSkillsPage
              onCreateModalChange={setIsPassiveSkillsCreateModalOpen}
              onMilestoneModalChange={setIsPassiveSkillsMilestoneModalOpen}
            />
          </div>
        )}

        {currentPage === 'shop' && (
          <div key={pageKey} className="page-enter">
            <ShopPage />
          </div>
        )}

        {currentPage === 'stats' && (
          <div key={pageKey} className="page-enter">
            <StatsPage />
          </div>
        )}

        {currentPage === 'history' && (
          <div key={pageKey} className="page-enter">
            <HistoryPage quests={quests} />
          </div>
        )}

        {currentPage === 'achievements' && (
          <div key={pageKey} className="page-enter">
            <AchievementsPage achievements={achievements} />
          </div>
        )}

        {currentPage === 'settings' && (
          <div key={pageKey} className="page-enter">
            <SettingsPage initialTab={settingsInitialTab} />
          </div>
        )}

        {currentPage === 'calendar' && (
          <div key={pageKey} className="page-enter">
            <CalendarPage />
          </div>
        )}
        </div>
      </div>

      {/* Level Up Modal */}
      {levelUpQueue.length > 0 && (
        <LevelUpModal
          levelUp={levelUpQueue[0]}
          onClose={handleLevelUp}
        />
      )}

      {/* Achievement Modal */}
      {achievementQueue.length > 0 && (
        <AchievementModal
          achievement={achievementQueue[0]}
          onClose={() => {
            hapticService.success();
            clearAchievementQueue();
          }}
        />
      )}

      {/* Create Quest Modal */}
      <CreateQuestModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          hapticService.tap();
          setIsCreateModalOpen(false);
        }}
        onCreate={(title, description, type, difficulty, resetDaily, scheduledFor, subTasks, scheduledTime, allDay) => {
          hapticService.success();
          createQuest(title, description, type, difficulty, resetDaily, scheduledFor, subTasks, scheduledTime, allDay);
        }}
      />

      {/* AI Generator Selector Modal */}
      {isAISelectorOpen && (
        <AIGeneratorSelectorModal
          onClose={() => {
            hapticService.tap();
            setIsAISelectorOpen(false);
          }}
          onSelectQuestGenerator={() => {
            hapticService.click();
            setIsAISelectorOpen(false);
            setIsAIGeneratorOpen(true);
          }}
          onSelectProgramGenerator={() => {
            hapticService.click();
            setIsAISelectorOpen(false);
            setIsProgramCreationOpen(true);
          }}
        />
      )}

      {/* AI Quest Generator Modal */}
      <AIQuestGenerator
        isOpen={isAIGeneratorOpen}
        onClose={() => {
          hapticService.tap();
          setIsAIGeneratorOpen(false);
        }}
        onQuestsGenerated={(aiQuests) => {
          hapticService.success();
          // Batch create all AI-generated quests at once
          createQuestsBatch(aiQuests.map(quest => ({
            title: quest.title,
            description: quest.description,
            type: quest.type,
            difficulty: quest.difficulty,
            resetDaily: quest.resetDaily,
            scheduledFor: undefined,
            subTasks: undefined
          })));
        }}
        onOpenSubscription={handleOpenSubscriptionSettings}
      />

      {/* Program Creation Modal */}
      <ProgramCreationModal
        isOpen={isProgramCreationOpen}
        onClose={() => {
          hapticService.tap();
          setIsProgramCreationOpen(false);
        }}
        onCreateProgram={async (data) => {
          hapticService.success();
          try {
            const programId = await createProgram(
              data.goal,
              data.duration,
              data.scheduleType,
              data.customDays,
              data.questsPerDay,
              data.intensity,
              data.domain,
              data.domainAnswers,
              data.aiFollowUpAnswers,
              data.preferredTimeStart,
              data.preferredTimeEnd,
              data.availableDays,
              data.skillLevel,
              data.timePerDay,
              data.learningStyle,
              data.specificFocus,
              data.availableResources
            );

            // Close the program creation modal
            setIsProgramCreationOpen(false);

            // Find the newly created program and open the roadmap modal
            if (programId) {
              const newProgram = programs.find(p => p.id === programId);
              if (newProgram) {
                setSelectedProgram(newProgram);
                setIsProgramRoadmapOpen(true);
              }
            }
          } catch (error) {
            // Error is already handled in createProgram
            console.error('Failed to create program:', error);
          }
        }}
      />

      {/* Program Roadmap Modal */}
      {selectedProgram && (
        <ProgramRoadmapModal
          isOpen={isProgramRoadmapOpen}
          onClose={() => {
            hapticService.tap();
            setIsProgramRoadmapOpen(false);
            setSelectedProgram(null);
          }}
          program={selectedProgram}
          quests={quests.filter(q => q.programId === selectedProgram?.id)}
        />
      )}

      {/* Quest Complete Animation */}
      {questCompleteData && (
        <QuestCompleteAnimation
          quest={questCompleteData.quest}
          rewards={questCompleteData.rewards}
          onComplete={clearQuestComplete}
        />
      )}

      {/* Penalty Warning Modal - Hidden in demo mode */}
      {!isDemoMode && (
        <PenaltyWarningModal
          isOpen={showPenaltyModal}
          failedQuests={failedDailyQuests}
          onClose={() => {}} // Penalty modal cannot be dismissed - must accept
          onAcceptPenalty={() => {
            hapticService.impact();
            acceptPenalty();
          }}
        />
      )}

      {/* Daily Reward Modal */}
      {dailyReward && (
        <DailyRewardModal
          isOpen={showDailyRewardModal}
          reward={dailyReward}
          onClaim={handleClaimDailyReward}
        />
      )}

      {/* Stat Allocation Modal */}
      <StatAllocationModal
        isOpen={isStatAllocationOpen}
        availablePoints={profile?.unallocatedStatPoints || 0}
        currentStats={profile?.stats || { strength: 0, intelligence: 0, agility: 0, vitality: 0, sense: 0 }}
        onAllocate={(allocations) => {
          hapticService.success();
          allocateStatPoints(allocations);
          setIsStatAllocationOpen(false);
        }}
        onClose={() => {
          hapticService.tap();
          setIsStatAllocationOpen(false);
        }}
      />

      {/* System Notifications */}
      <div className="fixed top-3 sm:top-4 right-3 sm:right-4 left-3 sm:left-auto z-[9998] flex flex-col gap-2 max-w-sm">
        {notifications.map((notification) => (
          <SystemNotification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => {
              hapticService.tap();
              removeNotification(notification.id);
            }}
            duration={5000}
          />
        ))}
      </div>

      {/* Onboarding Overlay */}
      {showOnboarding && (
        <OnboardingOverlay
          onComplete={handleCompleteOnboarding}
          onSkip={handleSkipOnboarding}
        />
      )}

      {/* Trial Expired Modal - Blocks all access (hide when subscription modal is open) */}
      {isSubscriptionExpired && !isSubscriptionModalOpen && (
        <TrialExpiredModal
          onUpgrade={() => {
            hapticService.click();
            setIsSubscriptionModalOpen(true);
          }}
          onLogout={() => {
            hapticService.click();
            signOut();
          }}
          previousTier={profile?.subscription?.previousTier || 'trial'}
        />
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => {
          hapticService.tap();
          setIsSubscriptionModalOpen(false);
        }}
        currentTier={profile?.subscription?.tier || 'expired'}
        subscriptionStatus={profile?.subscription?.status || 'trial'}
        trialDaysRemaining={0}
      />

      {/* Help Bubble - Hidden in demo mode and when any modal is open */}
      {!isDemoMode && !isAnyModalOpen && <HelpBubble />}
    </div>
  );
};

export default Dashboard;
